import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { showsAPI, importAPI, workspaceAPI, organisationsAPI } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import WorkspaceSettings from '../components/workspace/WorkspaceSettings';
import SectionSwitcher from '../components/layout/SectionSwitcher';
import { datetimeLocalToISO, isoToDatetimeLocal, formatShowDateTime } from '../lib/showTime';

const STATUS_COLOURS = {
  draft: 'text-gray-500 bg-gray-800',
  ready: 'text-blue-400 bg-blue-900/30',
  live: 'text-red-400 bg-red-900/30',
  complete: 'text-green-400 bg-green-900/30',
};

// scheduled_start is a timezone-independent (floating) datetime — see
// lib/showTime.js. Format and parse it on a fixed UTC basis so a show scheduled
// for a given wall-clock reads the same on every machine.
const formatDateTime = formatShowDateTime;
const toDatetimeLocal = isoToDatetimeLocal;

function CreateShowModal({ onClose, onCreate }) {
  const [name, setName] = useState('');
  const [sportType, setSportType] = useState('general');
  const [scheduledStart, setScheduledStart] = useState('');
  const [vmixHost, setVmixHost] = useState('');
  const [vmixPort, setVmixPort] = useState('8088');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleCreate(e) {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    setError('');

    try {
      const data = {
        name: name.trim(),
        sport_type: sportType,
        scheduled_start: scheduledStart ? datetimeLocalToISO(scheduledStart) : null,
        vmix_host: vmixHost.trim() || null,
        vmix_port: vmixPort ? parseInt(vmixPort) : 8088,
      };
      await onCreate(data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
      <div className="bg-[#111111] border border-[#2a2a2a] rounded-lg w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2a2a2a]">
          <h2 className="font-semibold text-white">New Show</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleCreate} className="p-5 space-y-4">
          {error && (
            <div className="px-3 py-2 bg-red-900/30 border border-red-700 rounded text-red-400 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">
              Show Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Premier League Final"
              required
              autoFocus
              className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">
              Sport Type
            </label>
            <select
              value={sportType}
              onChange={e => setSportType(e.target.value)}
              className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
            >
              <option value="general">General</option>
              <option value="football">Football/Soccer</option>
              <option value="rugby">Rugby</option>
              <option value="basketball">Basketball</option>
              <option value="cricket">Cricket</option>
              <option value="tennis">Tennis</option>
              <option value="golf">Golf</option>
              <option value="motorsport">Motorsport</option>
              <option value="climbing">Climbing</option>
              <option value="esports">Esports</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">
              Scheduled Start
            </label>
            <input
              type="datetime-local"
              value={scheduledStart}
              onChange={e => setScheduledStart(e.target.value)}
              className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">
              vMix Host (optional)
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={vmixHost}
                onChange={e => setVmixHost(e.target.value)}
                placeholder="192.168.1.x"
                className="flex-1 bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500"
              />
              <input
                type="number"
                value={vmixPort}
                onChange={e => setVmixPort(e.target.value)}
                placeholder="8088"
                className="w-20 bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !name.trim()}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-medium rounded transition-colors"
            >
              {loading ? 'Creating...' : 'Create Show'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Edit just the scheduled date/time of an existing show — e.g. after cloning,
// when go-live moves to a different day. Same datetime-local input + ISO format
// as the create-show form.
function EditDateModal({ show, onClose, onSave }) {
  const [value, setValue] = useState(() => toDatetimeLocal(show.scheduled_start));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSave(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await onSave(value ? datetimeLocalToISO(value) : null);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4" onClick={onClose}>
      <div className="bg-[#111111] border border-[#2a2a2a] rounded-lg w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2a2a2a]">
          <h2 className="font-semibold text-white">Scheduled Date</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSave} className="p-5 space-y-4">
          {error && (
            <div className="px-3 py-2 bg-red-900/30 border border-red-700 rounded text-red-400 text-sm">
              {error}
            </div>
          )}

          <p className="text-sm text-gray-400 truncate" title={show.name}>{show.name}</p>

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">
              Scheduled Start
            </label>
            <input
              type="datetime-local"
              value={value}
              onChange={e => setValue(e.target.value)}
              autoFocus
              className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-medium rounded transition-colors"
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const CSV_TEMPLATE = [
  'type,label,dur,script,notes,colour',
  'SEGMENT,Pre-Show,00:10:00,,Pre-show setup,',
  'SEGMENT,First Half,00:45:00,,Main content,',
  'BREAK,Half Time,00:15:00,,Advertisement break,#333333',
  'SEGMENT,Second Half,00:45:00,,Main content,',
  'GRAPHIC,Scoreboard,00:00:30,Show scoreboard graphic,,#6b21a8',
  'SEGMENT,Post-Show,00:05:00,,Wrap up,',
].join('\n');

function parseCSVRow(line) {
  const result = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    if (line[i] === '"') {
      inQuotes = !inQuotes;
    } else if (line[i] === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += line[i];
    }
  }
  result.push(current);
  return result;
}

function parseCSVPreview(text) {
  const lines = text.trim().split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return null;
  const headers = parseCSVRow(lines[0]).map(h => h.trim());
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVRow(lines[i]);
    const row = {};
    headers.forEach((h, idx) => {
      row[h.toLowerCase()] = (values[idx] || '').trim();
    });
    rows.push(row);
  }
  return { headers, rows, total: lines.length - 1 };
}

const CUE_TYPE_COLOURS = {
  SEGMENT: 'text-blue-400',
  CAMERA: 'text-green-400',
  REPLAY: 'text-orange-400',
  GRAPHIC: 'text-purple-400',
  AUDIO: 'text-yellow-400',
  RECORD_STREAM: 'text-red-400',
  NOTE: 'text-gray-400',
  BREAK: 'text-gray-500',
};

function ImportCsvModal({ shows, onClose, onImported }) {
  const [selectedShowId, setSelectedShowId] = useState(shows[0]?.id || '');
  const [file, setFile] = useState(null);
  const [clearExisting, setClearExisting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);

  function handleFile(f) {
    if (!f || !f.name.endsWith('.csv')) {
      setError('Please select a .csv file');
      return;
    }
    setFile(f);
    setResult(null);
    setError('');
    const reader = new FileReader();
    reader.onload = (evt) => {
      const parsed = parseCSVPreview(evt.target.result);
      if (!parsed || parsed.total === 0) {
        setError('No data rows found in CSV. Make sure the file has a header row and at least one data row.');
        setPreview(null);
      } else {
        setPreview(parsed);
      }
    };
    reader.readAsText(f);
  }

  function handleFileInput(e) {
    handleFile(e.target.files[0]);
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  }

  async function downloadTemplate() {
    try {
      const res = await api.get('/import/template.csv', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'rundown_template.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download template:', err);
    }
  }

  async function handleImport() {
    if (!file || !selectedShowId) return;
    setLoading(true);
    setError('');
    try {
      const res = await importAPI.csv(selectedShowId, file, clearExisting);
      setResult(res.data);
      onImported();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }

  const selectedShow = shows.find(s => s.id === selectedShowId);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
      <div className="bg-[#111111] border border-[#2a2a2a] rounded-lg w-full max-w-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2a2a2a] flex-shrink-0">
          <h2 className="font-semibold text-white">Import CSV Rundown</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {error && (
            <div className="px-3 py-2 bg-red-900/30 border border-red-700 rounded text-red-400 text-sm">
              {error}
            </div>
          )}

          {result ? (
            <div className="text-center py-6">
              <div className="w-12 h-12 bg-green-900/30 border border-green-700 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-white font-medium">Import successful</p>
              <p className="text-gray-400 text-sm mt-1">
                {result.imported} cue{result.imported !== 1 ? 's' : ''} added to <span className="text-white">{selectedShow?.name}</span>
              </p>
            </div>
          ) : (
            <>
              {/* Show selector */}
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">
                  Import into Show
                </label>
                <select
                  value={selectedShowId}
                  onChange={e => setSelectedShowId(e.target.value)}
                  className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                >
                  {shows.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>

              {/* File drop zone */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide">
                    CSV File
                  </label>
                  <button
                    type="button"
                    onClick={downloadTemplate}
                    className="text-xs text-blue-500 hover:text-blue-400 transition-colors flex items-center gap-1"
                  >
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download template
                  </button>
                </div>
                <label
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  className={`flex flex-col items-center justify-center w-full h-28 border-2 border-dashed rounded cursor-pointer transition-colors ${
                    dragOver ? 'border-blue-500 bg-blue-900/10' :
                    file ? 'border-green-700 bg-green-900/10' :
                    'border-[#2a2a2a] hover:border-[#3a3a3a]'
                  }`}
                >
                  <input type="file" accept=".csv" className="hidden" onChange={handleFileInput} />
                  {file ? (
                    <>
                      <svg className="w-5 h-5 text-green-400 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-sm text-green-400">{file.name}</span>
                      <span className="text-xs text-gray-500 mt-0.5">Click to change file</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-6 h-6 text-gray-600 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <span className="text-sm text-gray-500">Drop CSV here or <span className="text-blue-500">browse</span></span>
                      <span className="text-xs text-gray-600 mt-0.5">Columns: type, label, dur, script, notes, colour</span>
                    </>
                  )}
                </label>
              </div>

              {/* Replace toggle */}
              {selectedShow?._count?.cues > 0 && (
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <div
                    onClick={() => setClearExisting(v => !v)}
                    className={`relative w-9 h-5 rounded-full transition-colors ${clearExisting ? 'bg-amber-600' : 'bg-[#2a2a2a]'}`}
                  >
                    <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${clearExisting ? 'translate-x-4' : 'translate-x-0.5'}`} />
                  </div>
                  <span className="text-sm text-gray-300">
                    Replace existing cues
                    <span className="text-gray-500 ml-1">
                      (show has {selectedShow._count.cues} cue{selectedShow._count.cues !== 1 ? 's' : ''})
                    </span>
                  </span>
                </label>
              )}

              {/* Preview */}
              {preview && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Preview</span>
                    <span className="text-xs text-gray-500">{preview.total} row{preview.total !== 1 ? 's' : ''} detected</span>
                  </div>
                  <div className="border border-[#2a2a2a] rounded overflow-hidden">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-[#2a2a2a] bg-[#0a0a0a]">
                          {['Type', 'Label', 'Duration', 'Script'].map(h => (
                            <th key={h} className="px-3 py-2 text-left text-gray-500 font-medium">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {preview.rows.slice(0, 8).map((row, i) => {
                          const type = (row.cue_type || row.type || 'SEGMENT').toUpperCase();
                          return (
                            <tr key={i} className="border-b border-[#1a1a1a] last:border-0">
                              <td className="px-3 py-2">
                                <span className={`font-mono font-semibold text-[10px] ${CUE_TYPE_COLOURS[type] || 'text-gray-400'}`}>
                                  {type}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-white">{row.label || row.name || `Cue ${i + 1}`}</td>
                              <td className="px-3 py-2 text-gray-400 font-mono">{row.dur || row.duration || row.planned_duration || '—'}</td>
                              <td className="px-3 py-2 text-gray-500 truncate max-w-[200px]">{row.script || '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {preview.total > 8 && (
                      <div className="px-3 py-2 text-xs text-gray-600 bg-[#0a0a0a] border-t border-[#2a2a2a]">
                        + {preview.total - 8} more row{preview.total - 8 !== 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-5 py-4 border-t border-[#2a2a2a] flex-shrink-0">
          {result ? (
            <button
              onClick={onClose}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded transition-colors"
            >
              Done
            </button>
          ) : (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={!file || !selectedShowId || loading || !preview}
                className="px-5 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-medium rounded transition-colors"
              >
                {loading ? 'Importing...' : preview ? `Import ${preview.total} Cue${preview.total !== 1 ? 's' : ''}` : 'Import'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function MoveShowModal({ show, workspaces, onClose, onMoved }) {
  // A show can only move somewhere you could create one — not a workspace you are a guest in.
  const otherWorkspaces = workspaces.filter(ws => !ws.active && ws.role !== 'guest');
  const [targetId, setTargetId] = useState(otherWorkspaces[0]?.id || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleMove(e) {
    e.preventDefault();
    if (!targetId) return;
    setLoading(true);
    setError('');
    try {
      await showsAPI.move(show.id, targetId);
      onMoved(show.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to move show');
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
      <div className="bg-[#111111] border border-[#2a2a2a] rounded-lg w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2a2a2a]">
          <h2 className="font-semibold text-white">Move Show</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleMove} className="p-5 space-y-4">
          {error && (
            <div className="px-3 py-2 bg-red-900/30 border border-red-700 rounded text-red-400 text-sm">{error}</div>
          )}
          <div>
            <p className="text-sm text-gray-400 mb-3">
              Move <span className="text-white font-medium">"{show.name}"</span> to:
            </p>
            <select
              value={targetId}
              onChange={e => setTargetId(e.target.value)}
              className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
            >
              {otherWorkspaces.map(ws => (
                <option key={ws.id} value={ws.id}>{ws.name}</option>
              ))}
            </select>
          </div>
          <p className="text-xs text-gray-600 leading-relaxed">
            The show and all its cues will be moved. You can still access it by switching to that workspace.
          </p>
          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors">
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !targetId}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-medium rounded transition-colors"
            >
              {loading ? 'Moving…' : 'Move Show'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CreateWorkspaceModal({ organisationId, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleCreate(e) {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    setError('');
    try {
      // Created in the account that owns the open workspace, then opened.
      const created = await organisationsAPI.createWorkspace(organisationId, name.trim());
      const res = await workspaceAPI.switch(created.data.id);
      onCreated(res.data.token, res.data.user);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create workspace');
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
      <div className="bg-[#111111] border border-[#2a2a2a] rounded-lg w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2a2a2a]">
          <h2 className="font-semibold text-white">New Workspace</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleCreate} className="p-5 space-y-4">
          {error && (
            <div className="px-3 py-2 bg-red-900/30 border border-red-700 rounded text-red-400 text-sm">{error}</div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">
              Workspace Name
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Acme Sports"
              autoFocus
              className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500"
            />
          </div>
          <p className="text-xs text-gray-600 leading-relaxed">
            Creates a new workspace in this account and opens it. Everyone in the account can open it.
          </p>
          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors">
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !name.trim()}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-medium rounded transition-colors"
            >
              {loading ? 'Creating…' : 'Create Workspace'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function ShowDashboard() {
  const navigate = useNavigate();
  const { user, setAuth, logout } = useAuthStore();

  // What the open workspace lets this person do. The server enforces all of it;
  // these only hide what would be refused.
  const can = (capability) => !!user?.capabilities?.includes(capability);
  const canManageShows = can('show:manage');
  const canProduce = can('production:edit');
  const canManageAccount = can('org:manage');
  const hasWorkspace = !!user?.workspace_id;

  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showWorkspaceSettings, setShowWorkspaceSettings] = useState(false);
  const [showNewWorkspaceModal, setShowNewWorkspaceModal] = useState(false);
  const [movingShow, setMovingShow] = useState(null); // show object being moved
  const [editingDateShow, setEditingDateShow] = useState(null); // show whose date is being edited

  // Workspace list + dropdown
  const [allWorkspaces, setAllWorkspaces] = useState([]);
  const [wsDropdownOpen, setWsDropdownOpen] = useState(false);
  const wsDropdownRef = useRef(null);

  const loadShows = useCallback(async () => {
    try {
      setLoading(true);
      const res = await showsAPI.list();
      setShows(res.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load shows');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadWorkspaces = useCallback(async () => {
    workspaceAPI.mine()
      .then(r => setAllWorkspaces(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadWorkspaces();
    // No workspace open: nothing to list — the page offers a way forward instead.
    if (user?.workspace_id) loadShows();
    else setLoading(false);
  }, [loadShows, loadWorkspaces, user?.workspace_id]);

  // Close workspace dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (wsDropdownRef.current && !wsDropdownRef.current.contains(e.target)) {
        setWsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function handleSwitchWorkspace(workspaceId) {
    try {
      const res = await workspaceAPI.switch(workspaceId);
      setAuth(res.data.token, res.data.user);
      setWsDropdownOpen(false);
      window.location.reload();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to switch workspace');
    }
  }

  async function handleCreateShow(data) {
    const res = await showsAPI.create(data);
    setShows(prev => [res.data, ...prev]);
  }

  async function handleDeleteShow(e, showId) {
    e.stopPropagation();
    if (!confirm('Delete this show and all its cues? This cannot be undone.')) return;

    try {
      await showsAPI.delete(showId);
      setShows(prev => prev.filter(s => s.id !== showId));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete show');
    }
  }

  function handleOpenMove(e, show) {
    e.stopPropagation();
    setMovingShow(show);
  }

  function handleOpenEditDate(e, show) {
    e.stopPropagation();
    setEditingDateShow(show);
  }

  async function handleSaveDate(iso) {
    const res = await showsAPI.update(editingDateShow.id, { scheduled_start: iso });
    const next = res.data?.scheduled_start ?? iso;
    setShows(prev => prev.map(s => s.id === editingDateShow.id ? { ...s, scheduled_start: next } : s));
  }

  async function handleCloneShow(e, showId) {
    e.stopPropagation();
    try {
      const res = await showsAPI.clone(showId);
      setShows(prev => [res.data, ...prev]);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to clone show');
    }
  }

  async function handleResetShow(e, showId) {
    e.stopPropagation();
    if (!confirm('Reset this show to Draft? All cue statuses will be cleared.')) return;
    try {
      const res = await showsAPI.reset(showId);
      setShows(prev => prev.map(s => s.id === showId ? { ...s, status: res.data.status } : s));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to reset show');
    }
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Top Navigation */}
      <nav className="bg-[#111111] border-b border-[#2a2a2a] px-6 h-12 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 bg-red-600 rounded flex items-center justify-center">
            <div className="w-2.5 h-2.5 bg-white rounded-full" />
          </div>
          <span className="font-bold text-white text-sm">Mission Control</span>

          {/* Workspace selector */}
          <div className="relative" ref={wsDropdownRef}>
            <button
              onClick={() => setWsDropdownOpen(v => !v)}
              className="flex items-center gap-1.5 text-gray-400 hover:text-white text-sm transition-colors"
            >
              <span className="text-gray-600">/</span>
              <span>{user?.workspace_name || 'Workspace'}</span>
              {allWorkspaces.length > 1 && (
                <svg className={`w-3 h-3 transition-transform ${wsDropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              )}
            </button>

            {wsDropdownOpen && allWorkspaces.length > 1 && (
              <div className="absolute left-0 top-full mt-1 w-56 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg shadow-xl z-50 py-1">
                {allWorkspaces.map(ws => (
                  <button
                    key={ws.id}
                    onClick={() => handleSwitchWorkspace(ws.id)}
                    disabled={ws.active}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center gap-2 ${
                      ws.active
                        ? 'text-white bg-[#252525] cursor-default'
                        : 'text-gray-400 hover:text-white hover:bg-[#252525]'
                    }`}
                  >
                    {ws.active && <span className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />}
                    {!ws.active && <span className="w-1.5 h-1.5 flex-shrink-0" />}
                    <span className="truncate">{ws.name}</span>
                    <span className="ml-auto text-xs text-gray-600 capitalize">{ws.role}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <SectionSwitcher active="shows" />
        </div>

        <div className="flex items-center gap-4">
          {user?.avatar_url && (
            <img src={user.avatar_url} alt="" className="w-6 h-6 rounded-full" />
          )}
          <span className="text-gray-500 text-sm">{user?.name || user?.email}</span>
          <button
            onClick={() => navigate('/account')}
            title={user?.has_account ? 'Members, seats and workspaces' : 'Set up an account of your own'}
            className="text-gray-500 hover:text-white text-sm transition-colors"
          >
            Account
          </button>
          {hasWorkspace && (
            <button
              onClick={() => setShowWorkspaceSettings(true)}
              title="Workspace settings"
              className="text-gray-500 hover:text-white transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </button>
          )}
          <button
            onClick={logout}
            className="text-gray-500 hover:text-white text-sm transition-colors"
          >
            Sign Out
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-white">Shows</h1>
            <p className="text-gray-500 text-sm mt-0.5">
              {shows.length} show{shows.length !== 1 ? 's' : ''} in workspace
              {user?.role === 'guest' && (
                <span className="ml-2 text-xs text-amber-500">
                  You&rsquo;re a guest{user.guest_level === 'text' ? ' — scripts, notes, graphics text and running the show' : ' — full production access'}
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canProduce && (
            <button
              onClick={() => setShowImportModal(true)}
              disabled={shows.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-[#1a1a1a] hover:bg-[#222222] disabled:opacity-40 border border-[#2a2a2a] text-gray-300 text-sm font-medium rounded transition-colors"
              title={shows.length === 0 ? 'Create a show first before importing' : 'Import CSV rundown'}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
              </svg>
              Import
            </button>
            )}
            {canManageShows && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Show
            </button>
            )}
            {canManageAccount && (
            <button
              onClick={() => setShowNewWorkspaceModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#1a1a1a] hover:bg-[#222222] border border-[#2a2a2a] text-gray-300 text-sm font-medium rounded transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              New Workspace
            </button>
            )}
          </div>
        </div>

        {error && (
          <div className="mb-4 px-4 py-3 bg-red-900/30 border border-red-700 rounded text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Shows List */}
        {loading ? (
          <div className="text-center py-16 text-gray-600">Loading shows...</div>
        ) : !hasWorkspace ? (
          <div className="text-center py-16 max-w-md mx-auto">
            <div className="text-white font-medium mb-2">No workspace open</div>
            <p className="text-gray-500 text-sm mb-5 leading-relaxed">
              You don&rsquo;t have access to a workspace right now. If someone has invited you, open the invite link
              they sent. Or set up an account of your own to create shows.
            </p>
            <button
              onClick={() => navigate('/account')}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded transition-colors"
            >
              {user?.has_account ? 'Go to your account' : 'Set up your own account'}
            </button>
          </div>
        ) : shows.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-gray-600 mb-4">No shows yet</div>
            {canManageShows && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="text-blue-500 hover:text-blue-400 text-sm transition-colors"
              >
                Create your first show
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {shows.map(show => (
              <div
                key={show.id}
                onClick={() => navigate(`/shows/${show.id}`)}
                className="flex items-center gap-4 bg-[#111111] border border-[#2a2a2a] hover:border-[#3a3a3a] rounded-lg px-5 py-4 cursor-pointer transition-colors group"
              >
                {/* Status indicator */}
                <div className={`flex-shrink-0 px-2 py-0.5 rounded text-xs font-semibold uppercase ${STATUS_COLOURS[show.status]}`}>
                  {show.status}
                </div>

                {/* Show name + meta */}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-white group-hover:text-blue-400 transition-colors truncate">
                    {show.name}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                    <span className="capitalize">{show.sport_type}</span>
                    {show.scheduled_start && (
                      <span>{formatDateTime(show.scheduled_start)}</span>
                    )}
                    {show._count?.cues !== undefined && (
                      <span>{show._count.cues} cue{show._count.cues !== 1 ? 's' : ''}</span>
                    )}
                  </div>
                </div>

                {/* My role */}
                {show.my_role && (
                  <div className="text-xs text-gray-600 uppercase tracking-wide">
                    {show.my_role}
                  </div>
                )}

                {/* Reset button — only for complete shows */}
                {show.status === 'complete' && (
                  <button
                    onClick={(e) => handleResetShow(e, show.id)}
                    className="opacity-0 group-hover:opacity-100 p-1.5 text-gray-600 hover:text-amber-400 transition-all rounded text-xs font-semibold"
                    title="Reset show to Draft"
                  >
                    RESET
                  </button>
                )}

                {/* Edit scheduled date button */}
                {canProduce && (
                <button
                  onClick={(e) => handleOpenEditDate(e, show)}
                  className="opacity-0 group-hover:opacity-100 p-1.5 text-gray-600 hover:text-amber-400 transition-all rounded"
                  title="Edit scheduled date"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </button>
                )}

                {/* Clone button */}
                {canManageShows && (
                <button
                  onClick={(e) => handleCloneShow(e, show.id)}
                  className="opacity-0 group-hover:opacity-100 p-1.5 text-gray-600 hover:text-blue-400 transition-all rounded"
                  title="Clone show"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
                )}

                {/* Move to workspace button — only when user has multiple workspaces */}
                {canManageShows && allWorkspaces.filter(ws => ws.role !== 'guest').length > 1 && (
                  <button
                    onClick={(e) => handleOpenMove(e, show)}
                    className="opacity-0 group-hover:opacity-100 p-1.5 text-gray-600 hover:text-purple-400 transition-all rounded"
                    title="Move to another workspace"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                  </button>
                )}

                {/* Delete button */}
                {canManageShows && (
                <button
                  onClick={(e) => handleDeleteShow(e, show.id)}
                  className="opacity-0 group-hover:opacity-100 p-1.5 text-gray-600 hover:text-red-400 transition-all rounded"
                  title="Delete show"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
                )}

                {/* Arrow */}
                <svg className="w-4 h-4 text-gray-700 group-hover:text-gray-400 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            ))}
          </div>
        )}
      </div>

      {showCreateModal && (
        <CreateShowModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateShow}
        />
      )}

      {showImportModal && (
        <ImportCsvModal
          shows={shows}
          onClose={() => setShowImportModal(false)}
          onImported={loadShows}
        />
      )}

      {showWorkspaceSettings && (
        <WorkspaceSettings onClose={() => { setShowWorkspaceSettings(false); loadWorkspaces(); }} />
      )}

      {showNewWorkspaceModal && (
        <CreateWorkspaceModal
          organisationId={user?.organisation_id}
          onClose={() => setShowNewWorkspaceModal(false)}
          onCreated={(token, u) => { setAuth(token, u); window.location.reload(); }}
        />
      )}

      {editingDateShow && (
        <EditDateModal
          show={editingDateShow}
          onClose={() => setEditingDateShow(null)}
          onSave={handleSaveDate}
        />
      )}

      {movingShow && (
        <MoveShowModal
          show={movingShow}
          workspaces={allWorkspaces}
          onClose={() => setMovingShow(null)}
          onMoved={(showId) => {
            setShows(prev => prev.filter(s => s.id !== showId));
            setMovingShow(null);
          }}
        />
      )}
    </div>
  );
}
